// frontend/src/components/enterprice.tsx
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import ChatWidget from './ChatWidget'
import { ChatProvider } from '../contexts/ChatContext'
import { getUserPlanLimits, getUsagePercentage, formatLimit } from '../config/planLimits'
import './Dashboard.css';
// ✅ ADD THIS AT THE TOP with other imports
import "./enterprise/enterprise.css";

import {
  Mail,
  TrendingUp,
  Users,
  Zap,
  Sparkles,
  ChevronRight,
  Eye,
  Share2,
  Plus,
  Gift,
  Target,
  Settings,
  Bell,
  Crown,
  AlertCircle,
  RotateCcw,
} from 'lucide-react'
import CampaignCard from './CampaignCard'
import CampaignSubscribers from './CampaignSubscribers'
import Editor from './Editor'
import ProfilePanel from './settings/ProfilePanel'
import SettingsPanel from './settings/SettingsPanel'
import EnterpriseFeatures from './EnterpriseFeatures';

// ========== INTERFACES & TYPES ==========
interface NotificationData {
  type: 'success' | 'error'
  message: string
  id: number
}

export interface Campaign {
  id: string
  name: string
  subject: string
  status: 'draft' | 'sent' | 'scheduled'
  subscriberCount: number
  shareEnabled: boolean
  shareToken?: string
  shareUrl?: string
  viewCount: number
  sentAt: string | null
  scheduledFor: string | null
  updatedAt: string
  content: string
  createdAt?: string
}

interface UserData {
  name: string
  email?: string 
  plan?: string
  selectedPlan?: string
  avatar?: string
  profilePicture?: string
  photoURL?: string
  features?: string[]
}

interface DashboardState {
  campaigns: Campaign[]
  aiEmailsUsed: number
  loading: boolean
  error: string | null
  isRefreshing: boolean
}

// ========== CONSTANTS ==========
const INITIAL_STATE: DashboardState = {
  campaigns: [],
  aiEmailsUsed: 0,
  loading: false,
  error: null,
  isRefreshing: false,
}

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

// ========== MAIN COMPONENT ==========
export default function Dashboard() {
  const navigate = useNavigate()
  const { user, loading: authLoading, refreshUser } = useAuth()
  const { notifications, unreadCount, markAsRead } = useNotifications()

  // ========== REFS ==========
  const retryCountRef = useRef(0)
  const isMountedRef = useRef(true)
  const notificationTimeoutRef = useRef<NodeJS.Timeout>()

  // ========== STATE ==========
  const [state, setState] = useState<DashboardState>(INITIAL_STATE)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  const [selectedCampaignForEdit, setSelectedCampaignForEdit] = useState<Campaign | null>(null)
  const [notification, setNotification] = useState<NotificationData | null>(null)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)

  // ========== DERIVED VALUES ==========
  const typedUser = user as UserData | null
  const userPlan = typedUser?.selectedPlan || typedUser?.plan || 'starter'
  const planLimits = getUserPlanLimits(userPlan)
  const userName = typedUser?.name || 'User'
  const userInitial = userName?.charAt(0).toUpperCase() || 'U'
  const userAvatar = typedUser?.avatar || typedUser?.profilePicture || typedUser?.photoURL || null

  // ========== API CONFIG ==========
  const getApiConfig = () => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'
    if (!baseUrl) {
      console.error('API URL not configured')
    }
    return {
      BASE_URL: baseUrl,
      ENDPOINTS: {
        CAMPAIGNS: '/campaigns',
        AI_USAGE: '/user/ai-usage',
        SHARE: (id: string) => `/api/campaigns/${id}/share`,
      }
    }
  }

  const API_CONFIG = getApiConfig()

  // ========== UTILITY FUNCTIONS ==========
  const getAuthToken = (): string => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
    if (!token) {
      throw new Error('No authentication token found')
    }
    return token
  }

  const safeFormat = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return '0'
    if (value === Infinity) return 'Unlimited'
    return value.toLocaleString()
  }

  const apiFetch = async (endpoint: string, options: RequestInit = {}, timeout = 30000) => {
    const token = getAuthToken()
    
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        }
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('token')
          throw new Error('Session expired. Please login again.')
        }
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API Error: ${response.status}`)
      }

      return response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout. Please try again.')
      }
      throw error
    }
  }

  const getPlanDisplayName = (plan?: string): string => {
    if (!plan) return 'Starter'
    const names: Record<string, string> = {
      'starter': 'Starter',
      'pro': 'Professional',
      'premium': 'Premium',
      'enterprise': 'Enterprise'
    }
    return names[plan.toLowerCase()] || plan
  }

  // ========== NOTIFICATION FUNCTION ==========
  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current)
    }
    
    const id = Date.now()
    setNotification({ type, message, id })
    
    const timeoutId = setTimeout(() => {
      setNotification(prev => prev?.id === id ? null : prev)
    }, 4000)
    
    notificationTimeoutRef.current = timeoutId
    
    return () => clearTimeout(timeoutId)
  }, [])

  // ========== EFFECTS ==========
  
  // Dynamically load enterprise CSS
  

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowNotifications(false)
      }
    }
    
    if (showNotifications) {
      document.addEventListener('keydown', handleEscape)
    }
    
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showNotifications])

  // Fetch data
  const fetchData = useCallback(async (isRetry = false) => {
    if (!typedUser || !isMountedRef.current) return

    setState(prev => ({ 
      ...prev, 
      loading: !isRetry,
      error: null 
    }))
    
    try {
      const [campaignsData, usageData] = await Promise.allSettled([
        apiFetch(API_CONFIG.ENDPOINTS.CAMPAIGNS),
        apiFetch(API_CONFIG.ENDPOINTS.AI_USAGE)
      ])

      if (!isMountedRef.current) return

    const campaigns = campaignsData.status === 'fulfilled' 
  ? (campaignsData.value.data || campaignsData.value || []).map((c: any) => ({
      ...c,
      id: c._id || c.id
    }))
  : []
      
      const aiEmailsUsed = usageData.status === 'fulfilled' 
        ? (usageData.value.used || 0)
        : 0

      setState(prev => ({
        ...prev,
        campaigns,
        aiEmailsUsed,
        loading: false,
        error: null,
        isRefreshing: false,
      }))

      retryCountRef.current = 0
      
    } catch (error) {
      if (!isMountedRef.current) return
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to load data'
      
      if (retryCountRef.current < MAX_RETRIES && !isRetry) {
        retryCountRef.current++
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchData(true)
          }
        }, RETRY_DELAY * Math.pow(2, retryCountRef.current - 1))
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMessage,
          isRefreshing: false,
        }))
        showNotification('error', `${errorMessage}. Click refresh to try again.`)
        retryCountRef.current = 0
      }
    }
  }, [typedUser, showNotification])

  // Mount effect
  useEffect(() => {
    isMountedRef.current = true
    fetchData()
    
    return () => {
      isMountedRef.current = false
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current)
      }
    }
  }, [fetchData])

  // ========== CALLBACK HANDLERS ==========
  const handleLogout = useCallback(() => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('token')
    localStorage.removeItem('authToken')
    navigate('/auth')
  }, [navigate])


  const handleGenerateShare = useCallback(async (campaignId: string) => {
    const shareLimits: Record<string, number> = { starter: 100, pro: 500, professional: 500, premium: 500, enterprise: Infinity };
    const maxShares = shareLimits[userPlan] || 500;
    const currentShares = state.campaigns.filter(c => c.shareEnabled).length;
    if (currentShares >= maxShares) { showNotification('error', `Share limit reached (${maxShares}/month). Upgrade to Enterprise for unlimited!`); return; }
    const campaign = state.campaigns.find(c => c.id === campaignId || c._id === campaignId)
    if (!campaign) return

    const previousCampaigns = state.campaigns
    const optimisticToken = `share_${Date.now()}_${Math.random().toString(36).substring(7)}`
    
    setState(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c =>
        c.id === campaignId
          ? {
              ...c,
              shareEnabled: true,
              shareToken: optimisticToken,
              shareUrl: `${window.location.origin}/share/${optimisticToken}`,
            }
          : c
      ),
    }))

    try {
      const data = await apiFetch(API_CONFIG.ENDPOINTS.SHARE(campaignId), {
        method: 'POST',
      })

      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c =>
          c.id === campaignId
            ? {
                ...c,
                shareToken: data.data.shareToken,
                shareUrl: data.data.shareUrl,
              }
            : c
        ),
      }))
      showNotification('success', 'Share link generated! Copy it from the campaign card.')
    } catch (error) {
      setState(prev => ({
        ...prev,
        campaigns: previousCampaigns,
      }))
      const errorMsg = error instanceof Error ? error.message : 'Failed to generate share link'
      showNotification('error', errorMsg)
    }
  }, [state.campaigns, showNotification])

  const handleViewSubscribers = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId)
  }, [])


  const handleEditCampaign = useCallback((campaignId: string) => {
    const campaign = state.campaigns.find(c => c.id === campaignId || c._id === campaignId)

    if (campaign) {
      setSelectedCampaignForEdit(campaign)
    }
  }, [state.campaigns])

  const handleCreateNewCampaign = useCallback(() => {
    if (state.campaigns.length >= planLimits.maxCampaigns) {
      showNotification(
        'error',
        `You've reached the maximum campaigns limit (${planLimits.maxCampaigns}). Upgrade to create more.`
      )
      return
    }

    setSelectedCampaignForEdit({
      id: `camp_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      name: '',
      subject: '',
      status: 'draft',
      subscriberCount: 0,
      shareEnabled: false,
      viewCount: 0,
      sentAt: null,
      scheduledFor: null,
      updatedAt: new Date().toISOString(),
      content: '',
      createdAt: new Date().toISOString(),
    })
  }, [state.campaigns.length, planLimits.maxCampaigns, showNotification])

  const handleSaveCampaign = useCallback(async (campaign: Campaign) => {
    const isNew = !state.campaigns.find(c => c.id === campaign.id)
    const previousCampaigns = state.campaigns

    setState(prev => ({
      ...prev,
      campaigns: isNew
        ? [...prev.campaigns, { ...campaign, updatedAt: new Date().toISOString() }]
        : prev.campaigns.map(c =>
            c.id === campaign.id
              ? { ...campaign, updatedAt: new Date().toISOString() }
              : c
          ),
    }))

    try {
      const data = await apiFetch(
        isNew ? API_CONFIG.ENDPOINTS.CAMPAIGNS : `${API_CONFIG.ENDPOINTS.CAMPAIGNS}/${campaign.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          body: JSON.stringify(campaign),
        }
      )

      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c =>
          c.id === campaign.id
            ? { ...campaign, ...data.data, updatedAt: new Date().toISOString() }
            : c
        ),
      }))
setSelectedCampaignForEdit(null)
showNotification('success', 'Campaign saved!')
fetchData() // ← Reload campaigns from DB to get real IDs

    } catch (error) {
      setState(prev => ({
        ...prev,
        campaigns: previousCampaigns,
      }))
      const errorMsg = error instanceof Error ? error.message : 'Failed to save campaign'
      showNotification('error', errorMsg)
      throw error
    }
  }, [state.campaigns, showNotification])

  const handleUserUpdate = useCallback(() => {
    refreshUser()
  }, [refreshUser])

  const handleRefresh = useCallback(async () => {
    setState(prev => ({ ...prev, isRefreshing: true }))
    await fetchData()
    showNotification('success', 'Data refreshed!')
  }, [fetchData, showNotification])

  // ========== COMPUTED VALUES ==========
  const stats = useMemo(() => {
    const totalCampaigns = state.campaigns.length
    const totalSubscribers = state.campaigns.reduce((sum, c) => sum + (c.subscriberCount || 0), 0)
    const sharedCampaigns = state.campaigns.filter(c => c.shareEnabled).length
    const totalViews = state.campaigns.reduce((sum, c) => sum + (c.viewCount || 0), 0)

    return {
      totalCampaigns,
      totalSubscribers,
      sharedCampaigns,
      totalViews,
      conversionRate: totalViews > 0 ? ((totalSubscribers / totalViews) * 100).toFixed(1) : '0',
    }
  }, [state.campaigns])

  const aiUsage = useMemo(
    () => ({
      used: state.aiEmailsUsed,
      limit: planLimits.aiEmailsPerMonth,
      percentage: getUsagePercentage(state.aiEmailsUsed, planLimits.aiEmailsPerMonth),
    }),
    [state.aiEmailsUsed, planLimits.aiEmailsPerMonth]
  )

  const subscriberUsage = useMemo(
    () => ({
      used: stats.totalSubscribers,
      limit: planLimits.maxSubscribers,
      percentage: getUsagePercentage(stats.totalSubscribers, planLimits.maxSubscribers),
    }),
    [stats.totalSubscribers, planLimits.maxSubscribers]
  )

  const statCards = useMemo(
    () => [
      {
        label: 'Total Campaigns',
        value: stats.totalCampaigns ?? 0,
        icon: Mail,
        color: 'blue',
        bgColor: 'bg-blue-500',
        lightBg: 'bg-blue-50',
        subtext: (stats.sharedCampaigns ?? 0) > 0 ? `${stats.sharedCampaigns} shared` : 'Start sharing',
      },
      {
        label: 'Subscribers',
        value: safeFormat(stats.totalSubscribers),
        icon: Users,
        color: 'purple',
        bgColor: 'bg-purple-500',
        lightBg: 'bg-purple-50',
        subtext: (stats.totalSubscribers ?? 0) > 0 ? 'from share links' : '0 collected',
      },
      {
        label: 'Campaign Views',
        value: safeFormat(stats.totalViews),
        icon: Eye,
        color: 'green',
        bgColor: 'bg-green-500',
        lightBg: 'bg-green-50',
        subtext: (stats.totalViews ?? 0) > 0 ? 'across campaigns' : 'No views yet',
      },
      {
        label: 'Conversion Rate',
        value: `${stats.conversionRate ?? 0}%`,
        icon: TrendingUp,
        color: 'orange',
        bgColor: 'bg-orange-500',
        lightBg: 'bg-orange-50',
        subtext: 'subscribers/views',
      },
    ],
    [stats]
  )

  const planDisplayName = getPlanDisplayName(userPlan)

  // ========== CONDITIONAL RENDERS ==========
  if (selectedCampaignForEdit) {
    return (
      <Editor
        campaign={selectedCampaignForEdit}
        onSave={handleSaveCampaign}
        onClose={() => setSelectedCampaignForEdit(null)}
      />
    )
  }

  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  // ========== MAIN RENDER ==========
  return (
    <ChatProvider>
      <div className="dashboard-container"> 
        {/* Notification Toast */}
        {notification && (
          <div
            className={`notification-toast ${notification.type}`}
            role="alert"
            aria-live="polite"
          >
            <span className="notification-message">{notification.message}</span>
          </div>
        )}

        {state.error && (
          <div className="error-banner" role="alert">
            <div className="error-content">
              <div className="error-icon-wrapper">
                <AlertCircle className="error-icon" />
                <p className="error-text">{state.error}</p>
              </div>
              <button
                onClick={handleRefresh}
                className="retry-button"
                disabled={state.isRefreshing}
                aria-label="Retry loading data"
              >
                <RotateCcw className={`retry-icon ${state.isRefreshing ? 'spinning' : ''}`} />
                Retry
              </button>
            </div>
          </div>
        )}

        <header className="dashboard-header">
          <div className="header-container">
            
            {authLoading ? (
              <div className="avatar-skeleton"></div>
            ) : typedUser ? (
              <button
                onClick={() => setIsProfileOpen(true)}
                className="profile-button"
                title={`${userName} - ${planDisplayName} Plan`}
                aria-label="Open profile settings"
              >
                <div className="avatar-wrapper">
                  {userAvatar ? (
                    <img
                      src={userAvatar}
                      alt={userName}
                      className="avatar-image"
                    />
                  ) : (
                    userInitial
                  )}
                </div>
                <div className="profile-info">
                  <span className="profile-name">{userName}</span>
                  <div className="profile-plan">
                    {userPlan === 'enterprise' && <Crown className="crown-icon" />}
                    <span className="plan-name">{planDisplayName}</span>
                  </div>
                </div>
                <ChevronRight className="chevron-icon" />
              </button>
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="login-button"
              >
                Login
              </button>
            )}

            <div className="header-actions">
              
              {typedUser && (
                <button
                  onClick={handleRefresh}
                  disabled={state.isRefreshing}
                  className="icon-button"
                  title="Refresh data"
                  aria-label="Refresh dashboard data"
                >
                  <RotateCcw className={`icon ${state.isRefreshing ? 'spinning' : ''}`} />
                </button>
              )}

              <div className="notification-wrapper">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="notification-button"
                  aria-label="Notifications"
                  aria-expanded={showNotifications}
                  aria-haspopup="true"
                >
                  <Bell className="icon" />
                  {unreadCount > 0 && (
                    <span className="notification-badge" aria-label={`${unreadCount} unread notifications`}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <>
                    <div
                      className="notification-overlay"
                      onClick={() => setShowNotifications(false)}
                      aria-hidden="true"
                    ></div>
                    <div 
                      className="notification-dropdown"
                      role="dialog"
                      aria-label="Notifications panel"
                    >
                      <div className="dropdown-header">
                        <h3 className="dropdown-title">Notifications</h3>
                      </div>
                      <div className="dropdown-content">
                        {notifications.length === 0 ? (
                          <div className="empty-notifications">
                            <Bell className="empty-icon" />
                            <p>No new notifications</p>
                            <p className="empty-subtext">We'll notify you about campaign updates</p>
                          </div>
                        ) : (
                          notifications.slice(0, 5).map((notif) => (
                            <div
                              key={notif.id}
                              className={`notification-item ${!notif.read ? 'unread' : ''}`}
                              onClick={() => markAsRead(notif.id)}
                              role="button"
                              tabIndex={0}
                              onKeyPress={(e) => e.key === 'Enter' && markAsRead(notif.id)}
                            >
                              <p className="notification-title">{notif.title}</p>
                              <p className="notification-body">{notif.message}</p>
                              <p className="notification-time">
                                {new Date(notif.createdAt).toLocaleString()}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="dropdown-footer">
                          <button 
                            className="view-all-button"
                            onClick={() => {
                              setShowNotifications(false)
                              setIsSettingsOpen(true)
                            }}
                          >
                            View all notifications & settings
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {typedUser && (
                <button
                  onClick={() => setIsSettingsOpen(true)}
                  className="icon-button"
                  title="Billing & Settings"
                  aria-label="Settings"
                >
                  <Settings className="icon" />
                </button>
              )}
            </div>
          </div>
        </header>

        <ProfilePanel
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          user={typedUser as any}
          onLogout={handleLogout}
          onUserUpdate={handleUserUpdate}
        />

        <SettingsPanel
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          user={typedUser as any}
          onLogout={handleLogout}
          onUserUpdate={handleUserUpdate}
        />

        <main className="dashboard-main">
          
          <div className="hero-section">
            <h1 className="hero-title">Welcome back! 👋</h1>
            <p className="hero-subtitle">Here's what's happening with your campaigns today.</p>
          </div>

          <div className="upgrade-banner">
            <div className="upgrade-content">
              <div className="upgrade-info">
                <div className="upgrade-icon-wrapper">
                  {userPlan === 'enterprise' ? (
                    <Crown className="upgrade-icon crown" />
                  ) : (
                    <Sparkles className="upgrade-icon sparkles" />
                  )}
                </div>
                <div>
                  <h3 className="upgrade-title">You're on the {planDisplayName} Plan</h3>
                  <p className="upgrade-description">
                    {userPlan === 'enterprise'
                      ? 'Unlimited campaigns, subscribers, and AI generations!'
                      : userPlan === 'pro'
                      ? `Advanced features with ${formatLimit(planLimits.maxSubscribers)} subscribers!`
                      : 'Upgrade to Pro for more subscribers and advanced AI features!'}
                  </p>
                </div>
              </div>
              {userPlan !== 'enterprise' && (
                <button
                  onClick={() => navigate('/plan-selection')}
                  className="upgrade-button"
                >
                  Upgrade Now <ChevronRight className="button-icon" />
                </button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            {statCards.map((stat, index) => {
              const Icon = stat.icon
              return (
                <div key={index} className="stat-card">
                  <div className="stat-card-content">
                    <div className={`stat-icon-wrapper ${stat.lightBg}`}>
                      <Icon className={`stat-icon text-${stat.color}-600`} />
                    </div>
                    <span className="stat-value">{stat.value}</span>
                  </div>
                  <p className="stat-label">{stat.label}</p>
                  {stat.subtext && (
                    <p className="stat-subtext">{stat.subtext}</p>
                  )}
                </div>
              )
            })}
          </div>

          {/* Enterprise Features */}
          {(userPlan === 'enterprise' || userPlan === 'pro' || userPlan === 'professional') &&  (
            <EnterpriseFeatures 
              userPlan={userPlan as 'starter' | 'pro' | 'premium' | 'enterprise'}
              userName={userName}
              userEmail={typedUser?.email || user?.email || ''}
              stats={stats}
              campaigns={state.campaigns}
            />
          )}

          {/* Sharing Stats Highlight */}
          {stats.sharedCampaigns > 0 && (
            <div className="sharing-stats-grid">
              <div className="sharing-card green">
                <div className="sharing-card-header">
                  <div className="sharing-icon-wrapper green">
                    <Share2 className="sharing-icon" />
                  </div>
                  <h3 className="sharing-title">Your Campaigns Are Live! 🚀</h3>
                </div>
                <p className="sharing-stats">
                  {stats.sharedCampaigns} campaign{stats.sharedCampaigns !== 1 ? 's' : ''} shared • {stats.totalViews} view
                  {stats.totalViews !== 1 ? 's' : ''} • {stats.totalSubscribers} subscriber
                  {stats.totalSubscribers !== 1 ? 's' : ''}
                </p>
                <div className="conversion-rate">
                  {stats.conversionRate}% conversion
                </div>
              </div>

              <div className="sharing-card blue">
                <div className="sharing-card-header">
                  <div className="sharing-icon-wrapper blue">
                    <Target className="sharing-icon" />
                  </div>
                  <h3 className="sharing-title">Next Steps</h3>
                </div>
                <p className="next-steps-text">
                  Share more campaigns to grow your subscriber list
                </p>
                <button
                  onClick={handleCreateNewCampaign}
                  className="create-campaign-link"
                >
                  Create Campaign <ChevronRight className="link-icon" />
                </button>
              </div>
            </div>
          )}

          {/* Usage Meters */}
          <div className="usage-meters-grid">
            <div className="usage-card">
              <div className="usage-card-header">
                <div className="usage-icon-wrapper blue">
                  <Zap className="usage-icon" />
                </div>
                <div className="usage-info">
                  <h2 className="usage-title">AI Email Generation</h2>
                  <p className="usage-subtitle">
                    {planDisplayName} Plan: {planLimits.aiEmailsPerMonth === Infinity ? 'Unlimited' : `${safeFormat(planLimits.aiEmailsPerMonth)} emails/month`}
                  </p>
                </div>
                {planLimits.aiEmailsPerMonth !== Infinity && (
                  <span className={`usage-percentage ${aiUsage.percentage >= 90 ? 'red' : aiUsage.percentage >= 75 ? 'yellow' : 'blue'}`}>
                    {Math.round(aiUsage.percentage)}%
                  </span>
                )}
              </div>

              {planLimits.aiEmailsPerMonth !== Infinity ? (
                <>
                  <div className="progress-bar-container">
                    <div
                      className={`progress-bar ${aiUsage.percentage >= 90 ? 'red' : aiUsage.percentage >= 75 ? 'yellow' : 'blue'}`}
                      style={{ width: `${Math.min(100, aiUsage.percentage)}%` }}
                    ></div>
                  </div>
                  <div className="usage-stats">
                    <span className="usage-used">
                      {safeFormat(aiUsage.used)} / {safeFormat(aiUsage.limit)} used
                    </span>
                    <span className="usage-remaining">
                      {safeFormat(Math.max(0, aiUsage.limit - aiUsage.used))} remaining
                    </span>
                  </div>
                </>
              ) : (
                <div className="unlimited-usage">
                  <div className="unlimited-badge">
                    <Sparkles className="unlimited-icon" />
                    <span>Unlimited AI generations</span>
                  </div>
                  <p className="unlimited-text">
                    {safeFormat(aiUsage.used)} emails generated this month
                  </p>
                </div>
              )}
            </div>

            <div className="usage-card">
              <div className="usage-card-header">
                <div className="usage-icon-wrapper purple">
                  <Users className="usage-icon" />
                </div>
                <div className="usage-info">
                  <h2 className="usage-title">Subscribers Collected</h2>
                  <p className="usage-subtitle">
                    {planDisplayName} Plan: {formatLimit(planLimits.maxSubscribers)} max
                  </p>
                </div>
                {planLimits.maxSubscribers !== Infinity && (
                  <span className={`usage-percentage ${subscriberUsage.percentage >= 90 ? 'red' : subscriberUsage.percentage >= 75 ? 'yellow' : 'purple'}`}>
                    {Math.round(subscriberUsage.percentage)}%
                  </span>
                )}
              </div>

              {planLimits.maxSubscribers !== Infinity ? (
                <>
                  <div className="progress-bar-container">
                    <div
                      className={`progress-bar ${subscriberUsage.percentage >= 90 ? 'red' : subscriberUsage.percentage >= 75 ? 'yellow' : 'purple'}`}
                      style={{ width: `${Math.min(100, subscriberUsage.percentage)}%` }}
                    ></div>
                  </div>
                  <div className="usage-stats">
                    <span className="usage-used">
                      {safeFormat(subscriberUsage.used)} / {safeFormat(subscriberUsage.limit)} used
                    </span>
                    <span className="usage-remaining">
                      {safeFormat(Math.max(0, subscriberUsage.limit - subscriberUsage.used))} remaining
                    </span>
                  </div>
                </>
              ) : (
                <div className="unlimited-usage">
                  <div className="unlimited-badge">
                    <Users className="unlimited-icon" />
                    <span>Unlimited subscribers</span>
                  </div>
                  <p className="unlimited-text">
                    {safeFormat(stats.totalSubscribers)} subscribers collected
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upgrade Warning */}
          {(aiUsage.percentage >= 80 || subscriberUsage.percentage >= 80) && userPlan !== 'enterprise' && (
            <div className="upgrade-warning">
              <div className="warning-content">
                <Gift className="warning-icon" />
                <div>
                  <h3 className="warning-title">You're approaching your limits</h3>
                  <p className="warning-text">
                    {aiUsage.percentage >= 80 && `AI emails: ${aiUsage.used}/${aiUsage.limit} used. `}
                    {subscriberUsage.percentage >= 80 && `Subscribers: ${subscriberUsage.used.toLocaleString()}/${subscriberUsage.limit.toLocaleString()} used.`}
                  </p>
                  <button
                    onClick={() => navigate('/plan-selection')}
                    className="warning-upgrade-link"
                  >
                    Upgrade to {userPlan === 'starter' ? 'Pro' : 'Enterprise'} for higher limits →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recent Campaigns */}
          <div className="campaigns-section">
            <div className="campaigns-header">
              <div>
                <h2 className="campaigns-title">Recent Campaigns</h2>
                <p className="campaigns-subtitle">
                  {stats.totalCampaigns} campaign{stats.totalCampaigns !== 1 ? 's' : ''} • {stats.sharedCampaigns} shared
                </p>
              </div>
              <button
                onClick={handleCreateNewCampaign}
                className="new-campaign-button"
              >
                <Plus className="button-icon" />
                New Campaign
              </button>
            </div>

            {state.loading ? (
              <div className="campaigns-loading">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading campaigns...</p>
              </div>
            ) : state.campaigns.length === 0 ? (
              <div className="empty-campaigns">
                <div className="empty-icon">📧</div>
                <h3 className="empty-title">No campaigns yet</h3>
                <p className="empty-text">Create your first AI-powered email campaign and share it to collect subscribers!</p>
                <button
                  onClick={handleCreateNewCampaign}
                  className="empty-create-button"
                >
                  <Zap className="button-icon" />
                  Create Campaign
                </button>
              </div>
            ) : (
              <div className="campaigns-grid">
                {state.campaigns.map((campaign) => (
                  <CampaignCard
                  key={campaign.id || campaign._id || `camp-${index}` }
                    campaign={campaign}
                    onViewSubscribers={handleViewSubscribers}
                    onGenerateShare={handleGenerateShare}
                    onEditCampaign={handleEditCampaign}
                    onUpgrade={() => navigate('/plan-selection')}
                    totalActiveShares={stats.sharedCampaigns}
                    totalSubscribers={stats.totalSubscribers}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick Tips */}
          <div className="tips-grid">
            <div className="tip-card blue">
              <div className="tip-content">
                <div className="tip-icon-wrapper blue">
                  <Share2 className="tip-icon" />
                </div>
                <div>
                  <h3 className="tip-title">Share Your Campaign</h3>
                  <p className="tip-text">
                    Generate a share link and share it on social media to collect subscribers instantly.
                  </p>
                </div>
              </div>
            </div>

            <div className="tip-card green">
              <div className="tip-content">
                <div className="tip-icon-wrapper green">
                  <TrendingUp className="tip-icon" />
                </div>
                <div>
                  <h3 className="tip-title">Track Your Growth</h3>
                  <p className="tip-text">
                    Monitor how many people view and subscribe to your campaigns. Higher engagement = better campaigns!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Summary */}
          <div className="plan-summary">
            <div className="plan-summary-content">
              <div>
                <h3 className="plan-summary-title">{planDisplayName} Plan Summary</h3>
                <div className="plan-stats">
                  <span className="plan-stat">
                    <Mail className="stat-icon blue" />
                    <span className="stat-value">{stats.totalCampaigns}</span> campaign
                    {stats.totalCampaigns !== 1 ? 's' : ''}
                  </span>
                  <span className="plan-stat">
                    <Users className="stat-icon purple" />
                    <span className="stat-value">{safeFormat(stats.totalSubscribers)}</span>
                    {planLimits.maxSubscribers !== Infinity && `/${formatLimit(planLimits.maxSubscribers)}`} subscribers
                  </span>
                  <span className="plan-stat">
                    <Share2 className="stat-icon green" />
                    <span className="stat-value">{stats.sharedCampaigns}</span> shared
                  </span>
                </div>
              </div>
            </div>
          </div>
        </main>

        {selectedCampaignId && (
          <CampaignSubscribers
            campaign={state.campaigns.find(c => c.id === selectedCampaignId || c._id === selectedCampaignId)!}
            onClose={() => setSelectedCampaignId(null)}
            onUpgrade={() => navigate('/plan-selection')}
          />
        )}
        <ChatWidget />
      </div>
    </ChatProvider>
  )
} 
